/**
 * PublicApplyNowDialog — the "Apply Now" lead-capture dialog rendered on the
 * public school landing page. Submits to `/api/public/schools/{slug}/inquiries`
 * which is rate-limited (3 submissions / IP / minute) on the backend.
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, GraduationCap, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicApplyNowDialog({ open, onOpenChange, slug, schoolName, branches = [], palette }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    student_name: "", student_age: "",
    branch_id: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/schools/${slug}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        student_name: form.student_name.trim(),
        student_age: form.student_age.trim(),
        branch_id: form.branch_id || null,
        message: form.message.trim(),
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      if (status === 429) {
        setErrors({ form: "Too many submissions from this device. Please wait a minute and try again." });
      } else {
        setErrors({ form: typeof detail === "string" ? detail : "Submission failed. Please try again." });
      }
    } finally { setSubmitting(false); }
  }

  function handleOpenChange(v) {
    if (!v) {
      setSuccess(false);
      setForm({ name: "", email: "", phone: "", student_name: "", student_age: "", branch_id: "", message: "" });
      setErrors({});
    }
    onOpenChange(v);
  }

  const accent = palette?.text || "#1D4ED8";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="apply-now-dialog">
        {success ? (
          <div className="text-center py-6" data-testid="apply-now-success">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#DCFCE7] flex items-center justify-center mb-3">
              <CheckCircle2 className="w-9 h-9 text-[#15803D]" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-foreground">Thanks — we got it!</h3>
            <p className="text-slate-600 mt-2 max-w-sm mx-auto">
              The team at {schoolName} has been notified and will reach out at the email or phone you provided.
            </p>
            <Button
              onClick={() => handleOpenChange(false)}
              className="mt-6 rounded-full h-11 px-6 text-white"
              style={{ background: `linear-gradient(90deg, ${palette?.from || accent}, ${palette?.to || accent})` }}
              data-testid="apply-now-done-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                <GraduationCap className="w-5 h-5" style={{ color: accent }} />
                Apply to {schoolName}
              </DialogTitle>
              <DialogDescription>
                Tell us a bit about yourself and we&apos;ll get back to you with next steps.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="apply-now-form">
              <div>
                <Label htmlFor="apply-name">Your name <span className="text-red-500">*</span></Label>
                <Input
                  id="apply-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  maxLength={120}
                  data-testid="apply-name-input"
                  autoComplete="name"
                />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="apply-email">Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="apply-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    maxLength={200}
                    data-testid="apply-email-input"
                    autoComplete="email"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="apply-phone">Phone</Label>
                  <Input
                    id="apply-phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    maxLength={40}
                    placeholder="Optional"
                    data-testid="apply-phone-input"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="apply-student">Student&apos;s name</Label>
                  <Input
                    id="apply-student"
                    value={form.student_name}
                    onChange={(e) => set("student_name", e.target.value)}
                    maxLength={120}
                    placeholder="Optional"
                    data-testid="apply-student-input"
                  />
                </div>
                <div>
                  <Label htmlFor="apply-age">Age / Grade</Label>
                  <Input
                    id="apply-age"
                    value={form.student_age}
                    onChange={(e) => set("student_age", e.target.value)}
                    maxLength={40}
                    placeholder="e.g. 6 yrs / Grade 1"
                    data-testid="apply-age-input"
                  />
                </div>
              </div>

              {branches.length > 1 && (
                <div>
                  <Label>Preferred branch</Label>
                  <Select value={form.branch_id || "any"} onValueChange={(v) => set("branch_id", v === "any" ? "" : v)}>
                    <SelectTrigger data-testid="apply-branch-select">
                      <SelectValue placeholder="No preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">No preference</SelectItem>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="apply-message">Message</Label>
                <Textarea
                  id="apply-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Any questions or details you'd like us to know? (Optional)"
                  data-testid="apply-message-input"
                />
              </div>

              {errors.form && (
                <p className="text-sm text-red-600 bg-red-50 rounded-2xl px-3 py-2" data-testid="apply-form-error">{errors.form}</p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} data-testid="apply-cancel-btn">Cancel</Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full px-6 text-white"
                  style={{ background: `linear-gradient(90deg, ${palette?.from || accent}, ${palette?.to || accent})` }}
                  data-testid="apply-submit-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send application"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
